import {
  INTAKE_FORM_SECTIONS,
  INTAKE_FORM_SECTIONS_MAPPER,
  PHI_RELEASE_CONSENT_FORM,
  TELEHEALTH_CONSENT_FORM,
} from "@/pages/app/provider/scheduling/appointment/appointment-constant";
import {
  IntakeFormSectionOption,
  hasIntakeForm,
} from "@/pages/app/provider/scheduling/intake-form/intake-form-utils";
import { PatientIntakeForm } from "@/services";
import {
  APPOINTMENT_FORMS_TEXT,
  APPOINTMENT_FORM_SUBMITTED_STATUS,
} from "./appointment-additional-forms-constant";
import {
  AppointmentFormKind,
  AppointmentFormRow,
  FormsAndAssessmentsItem,
  IntakePreviewData,
} from "./appointment-additional-forms-types";

/** Every form the preview can show, so API names, labels and old aliases all resolve to one section. */
const KNOWN_SECTIONS: IntakeFormSectionOption[] = [
  TELEHEALTH_CONSENT_FORM,
  PHI_RELEASE_CONSENT_FORM,
  ...INTAKE_FORM_SECTIONS,
];

/** The section a form name belongs to, by key, label or alias; unknown names stand on their own. */
export const sectionFor = (name: string): IntakeFormSectionOption =>
  KNOWN_SECTIONS.find((section) => hasIntakeForm([name], section)) ?? {
    value: name,
    label: name,
  };

/** Turns an internal form token (e.g. "physicianPatientArbitrationAgreement") into a readable label. */
export const formatFormName = (name?: string): string => {
  if (!name) return APPOINTMENT_FORMS_TEXT.placeholder;
  return (
    INTAKE_FORM_SECTIONS_MAPPER[name] ??
    sectionFor(name).label ??
    name
      .replace(/_/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/(?:^|\s)(\w)/g, (match) => match.toUpperCase())
  );
};

export const findIntakePreview = (
  submissions: PatientIntakeForm[],
  sectionName: string
): IntakePreviewData | undefined => {
  const section = sectionFor(sectionName);
  const match = submissions.find((it) => hasIntakeForm(it.formName, section));
  if (!match) return undefined;
  return {
    content: match.content,
    activeSections: match.activeSections,
    formName: match.formName,
  };
};

/** A form is previewable when the API marks it completed or a submission for it exists. */
export const toFormRows = (
  items: FormsAndAssessmentsItem[] | undefined,
  kind: AppointmentFormKind,
  submissions: PatientIntakeForm[]
): AppointmentFormRow[] =>
  (items ?? []).map((item, index) => {
    const name = item.name ?? "";
    const section = sectionFor(name);
    return {
      id: item.uuid ?? `${kind}-${name || index}`,
      kind,
      name: section.value,
      label: formatFormName(name),
      isSubmitted:
        item.status === APPOINTMENT_FORM_SUBMITTED_STATUS ||
        !!findIntakePreview(submissions, name),
    };
  });
